import { GoogleGenAI, Type } from "@google/genai";
import { TestResult, TestLogEntry, AIActionPlanStep, SeasonalPlan, ProductDeal, WaterBodyType } from "../types";

export interface LsiAnalysisInput {
  lsi: number;
  ph: number;
  temp: number;
  ch: number;
  ta: number;
  cya: number;
  tds: number;
  TF: number;
  CF_Ca: number;
  AF: number;
  CF_TDS: number;
  correctedAlk: number;
}

/** Thrown when the Gemini API key is not configured at build time. */
export class PoolAiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PoolAiError';
  }
}

let aiClient: GoogleGenAI | null = null;
function getAi(): GoogleGenAI {
  const apiKey: unknown = import.meta.env.VITE_GEMINI_API_KEY;
  if (typeof apiKey !== 'string' || apiKey.length === 0) {
    throw new PoolAiError(
      'AI is not configured (VITE_GEMINI_API_KEY was missing at build time). All non-AI features still work.',
    );
  }
  if (!aiClient) aiClient = new GoogleGenAI({ apiKey });
  return aiClient;
}

const model = "gemini-2.5-flash";

const METRIC_DOSE_RULE =
  'Every dosage you write MUST include the metric equivalent in parentheses: millilitres for liquids (1 fl oz = 29.57 ml, 1 quart = 946 ml, 1 gallon = 3785 ml) and grams for dry/powder chemicals (1 oz = 28.35 g, 1 lb = 453.6 g). Always use ml (never litres/L) and g (never kg). Example: "Add 16 fl oz (473 ml) of Muriatic Acid." or "Add 1.5 lbs (680 g) of Sodium Bicarbonate." or "Add 1 gallon (3785 ml) of liquid chlorine."';

const actionPlanSchema = {
    type: Type.ARRAY,
    items: {
      type: Type.OBJECT,
      properties: {
        parameter: { type: Type.STRING, description: "The chemical parameter to address (e.g., 'Low Free Chlorine', 'High pH')." },
        currentValue: { type: Type.STRING, description: "The current measured value of the parameter (e.g., '1.0 ppm', '8.0')." },
        idealRange: { type: Type.STRING, description: "The ideal range for this parameter (e.g., '2.0-4.0 ppm', '7.4-7.6')." },
        action: { type: Type.STRING, description: "A clear, concise, one-sentence action to take (e.g., 'Add a sanitizer to raise Free Chlorine.', 'Add pH Down to lower the pH.')." },
        dosages: { type: Type.ARRAY, items: { type: Type.STRING }, description: "An array of specific dosage instructions based on the pool volume. Be precise and ALWAYS include the metric equivalent in parentheses (ml for liquids, grams for dry chemicals). For example: 'Add 1.5 lbs (680 g) of Cal-Hypo shock.' or 'Add 1 quart (946 ml) of Muriatic Acid.' If no dosage is needed, return an empty array." },
        chemical: { type: Type.STRING, description: "The generic chemical name recommended for the dosage (e.g., 'Calcium Hypochlorite', 'Muriatic Acid', 'Sodium Bicarbonate')." },
        notes: { type: Type.STRING, description: "A brief, helpful note or rationale for the action. For example: 'Shocking is necessary to eliminate contaminants and free up chlorine.' or 'High pH reduces chlorine effectiveness and can cause scaling.'" },
      },
      required: ["parameter", "currentValue", "idealRange", "action", "dosages", "chemical", "notes"],
    }
};

const seasonalPlanSchema = {
    type: Type.OBJECT,
    properties: {
        planType: { type: Type.STRING, enum: ['Winterizing', 'Summer Start-up'] },
        steps: {
            type: Type.ARRAY,
            items: {
                type: Type.OBJECT,
                properties: {
                    step: { type: Type.NUMBER, description: "The step number in the sequence." },
                    title: { type: Type.STRING, description: "A concise title for the step (e.g., 'Balance the Water', 'Add Winterizing Chemicals')." },
                    description: { type: Type.STRING, description: "A detailed paragraph explaining what to do in this step." },
                    chemicalsNeeded: { type: Type.ARRAY, items: { type: Type.STRING }, description: "A list of generic chemicals or equipment needed for this step." },
                    importantNote: { type: Type.STRING, description: "An optional but highly recommended crucial tip or warning for this step." },
                },
                required: ["step", "title", "description", "chemicalsNeeded"],
            },
        },
    },
    required: ["planType", "steps"],
};

/**
 * Build a version of TestResult / TestLogEntry with only the fields that
 * were actually tested this session (i.e. not null).  Null fields mean
 * "not tested" — we never send them to the AI so it doesn't raise false
 * alarms about untested parameters.
 */
function testedFields(r: TestResult | TestLogEntry): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(r)) {
    if (val !== null) out[key] = val;
  }
  return out;
}

export const generateAIActionPlan = async (
  results: TestResult | TestLogEntry,
  poolVolume: number,
  waterBody: WaterBodyType = 'pool',
  history: TestLogEntry[] = [],
): Promise<AIActionPlanStep[]> => {
    const isHotTub = waterBody === 'hot_tub';
    const bodyLabel = isHotTub ? 'hot tub / spa' : 'swimming pool';
    const fcIdeal = isHotTub ? '3–5 ppm' : '2–6 ppm (minimum 7.5% of CYA level)';
    const chIdeal = isHotTub ? '150–250 ppm' : '200–400 ppm';
    const cyaNote = isHotTub
      ? 'CYA is generally not recommended for hot tubs/spas using chlorine — if present, keep under 50 ppm.'
      : 'CYA (Stabilizer): 30–50 ppm. CYA protects chlorine from UV degradation. Minimum FC should be ~7.5% of CYA (e.g., CYA=40 → FC min=3 ppm). High CYA (>90 ppm) severely reduces chlorine effectiveness and can only be corrected by partial drain and refill.';

    // Build trend analysis block from history
    const recentHistory = history.slice(-5);
    let trendBlock = '';
    if (recentHistory.length >= 2) {
      const historyLines = recentHistory.map(h =>
        `  ${h.timestamp}: FC=${h.fc ?? '?'} CC=${h.cc ?? '?'} pH=${h.ph ?? '?'} TA=${h.ta ?? '?'} CH=${h.ch ?? '?'} CYA=${h.cya ?? '?'} Temp=${h.temp ?? '?'}°F`
      ).join('\n');
      trendBlock = `\n\nRecent test history (oldest first, newest last):\n${historyLines}\n\nAnalyze these trends: Is pH consistently drifting high or low? Is chlorine repeatedly low (indicating a demand issue, algae risk, or CYA problem)? Is TA unstable? Call out any concerning patterns you observe and factor them into your recommendations.`;
    }

    const systemInstruction = `You are a world-class pool and spa water chemistry expert with 20+ years of experience. Your task is to analyze water test results for a ${bodyLabel} and generate a comprehensive, expert-level, prioritized action plan.

WATER BODY: ${bodyLabel.toUpperCase()} (${poolVolume.toLocaleString()} gallons)

TREATMENT ORDER (always follow this sequence):
1. Extremely high FC (>10 ppm) — stop all chlorination immediately
2. Combined Chlorine (CC ≥ 0.5 ppm) — shock treatment required
3. Total Alkalinity (TA) — fix this BEFORE pH; TA acts as a pH buffer
4. pH — adjust after TA is stable
5. Free Chlorine (FC) — sanitizer level
6. Calcium Hardness (CH)
7. Cyanuric Acid (CYA) ${isHotTub ? '(rarely needed for hot tubs)' : ''}

IDEAL RANGES for a ${bodyLabel}:
- Free Chlorine (FC): ${fcIdeal}
- Combined Chlorine (CC): < 0.5 ppm. If ≥ 0.5 ppm, breakpoint chlorination is required (add 10× the CC level in FC).
- pH: 7.4–7.6 (7.4 is ideal — best chlorine efficiency, least eye irritation)
- Total Alkalinity (TA): 80–120 ppm
- Calcium Hardness (CH): ${chIdeal}
- ${cyaNote}
- Water Temperature: ${isHotTub ? '100–104°F max for safety; higher temps dramatically accelerate chlorine loss' : 'affects chemical reaction rates and evaporation'}

IMPORTANT RULES:
- If FC is above range (> ${isHotTub ? '5' : '6'} ppm but ≤ 10 ppm): Stop all chlorination, keep pump running, expose water to sunlight. Do NOT swim/soak. Retest every 4–6 hours.
- If FC > 10 ppm: Same as above PLUS consider Sodium Thiosulfate neutralizer (2 oz per 10,000 gal lowers FC ~1 ppm).
- Always fix TA before pH. An unstable TA causes pH to "bounce" even after correction.
- ${isHotTub ? 'Hot tub water heats up chlorine loss — expect to need more frequent chlorine additions. Drain and refill every 3–4 months.' : 'Shocking should always be done at dusk or night to prevent UV from destroying the chlorine before it can work.'}
- Provide exact product names, doses (calculated for ${poolVolume} gallons), and step-by-step instructions.
- ${METRIC_DOSE_RULE}
- Explain WHY each out-of-range parameter is a problem — what are the consequences?
- Include specific timing: when to retest, when it is safe to return to the water.
- Include any chemical interaction warnings (e.g., never add acid and chlorine at the same time).
- If all parameters are in range, return an empty array and congratulate the user.

For the "notes" field: provide a detailed expert explanation covering (1) why this parameter matters, (2) what happens if left uncorrected, and (3) any relevant warnings or tips specific to a ${bodyLabel}.

Respond ONLY with the JSON array conforming to the provided schema.`;

    const testedOnly = testedFields(results);
    const prompt = `Generate a detailed, expert action plan for a ${poolVolume}-gallon ${bodyLabel} with these test results: ${JSON.stringify(testedOnly)}${trendBlock}\n\nIMPORTANT: Only the parameters shown above were actually tested this session. Do NOT flag, comment on, or recommend dosing for any parameter that is not present in the JSON above.`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: {
                systemInstruction,
                responseMimeType: "application/json",
                responseSchema: actionPlanSchema,
            }
        });

        const jsonText = (response.text ?? '').trim();
        if (!jsonText) {
             console.warn("Gemini API returned an empty response for action plan.");
             return [];
        }

        const plan = JSON.parse(jsonText);
        return plan as AIActionPlanStep[];
    } catch (error) {
        console.error("Error generating AI action plan:", error);
        throw new Error("Failed to generate an AI action plan. The model may be unavailable or the request failed.");
    }
};

export const queryLogWithAI = async (log: TestLogEntry[], question: string, poolVolume: number): Promise<string> => {
    const systemInstruction = `You are a helpful AI pool chemistry expert. Analyze the user's pool water chemistry data log to answer their question.
- Base your answers *only* on the provided data log.
- If the question involves chemical dosages, you MUST use the provided pool volume of ${poolVolume} gallons for your calculations.
- Use standard dosage formulas. For example:
  - Raising TA 10ppm in 10k gallons requires ~1.5 lbs of Sodium Bicarbonate.
  - Lowering pH by 0.2 in 10k gallons requires ~16 oz of Sodium Bisulfate or ~16 fl oz of Muriatic Acid.
  - Raising CH 10ppm in 10k gallons requires ~1.2 lbs of Calcium Chloride.
  - Raising FC 1ppm in 10k gallons with 68% Cal-Hypo requires ~2.35 oz.
- ${METRIC_DOSE_RULE}
- When asked about a specific result (e.g., "my last result"), use the last entry in the log.
- Be concise and clear. If the data is insufficient to answer the question, state that.`;
    const prompt = `Here is the pool test log (most recent entry is last):\n${JSON.stringify(log)}\n\nMy pool is ${poolVolume} gallons.\n\nQuestion: "${question}"`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: { systemInstruction }
        });
        return response.text ?? '';
    } catch (error) {
        console.error("Error querying log with AI:", error);
        throw new Error("Failed to get an answer from the AI. Please try again.");
    }
};

export const getSeasonalSuggestion = async (location: string): Promise<string> => {
    const systemInstruction = `You are a helpful pool care assistant. Based on the user's location and the current date, suggest whether it's time to prepare for opening or closing their pool. Be concise and provide a brief rationale based on typical seasonal weather.`;
    const prompt = `My location is ${location}. The current date is ${new Date().toLocaleDateString('en-US')}. Is it a good time to think about opening or closing my pool for the season?`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: { systemInstruction }
        });
        return response.text ?? '';
    } catch (error) {
        console.error("Error getting seasonal suggestion:", error);
        throw new Error("Failed to get a suggestion from the AI.");
    }
};

export const generateSeasonalPlan = async (task: 'Winterizing' | 'Summer Start-up', location: string, poolVolume: number): Promise<SeasonalPlan> => {
    const systemInstruction = `You are a world-class pool care expert. Create a detailed, step-by-step guide for the user to perform a seasonal pool task. Tailor the advice for their location and pool size. Be clear, safe, and comprehensive. Respond ONLY with the JSON object conforming to the schema.`;
    const prompt = `Generate a detailed "${task}" plan for a ${poolVolume} gallon swimming pool located in ${location}.`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: {
                systemInstruction,
                responseMimeType: "application/json",
                responseSchema: seasonalPlanSchema,
            }
        });
        const jsonText = (response.text ?? '').trim();
        return JSON.parse(jsonText);
    } catch (error) {
        console.error(`Error generating ${task} plan:`, error);
        throw new Error(`Failed to generate the ${task} plan.`);
    }
};

export const findProductDeals = async (productName: string): Promise<{ deals: ProductDeal[], citations: unknown[] }> => {
    const systemInstruction = `You are an AI shopping assistant. The user will provide a pool chemical product name. Your task is to use Google Search to find up to 3 online stores selling this product. For each result, provide the store name, the price, and the direct URL to the product page.`;
    const prompt = `Find online deals for the pool chemical: "${productName}"`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: { systemInstruction, tools: [{ googleSearch: {} }] },
        });

        const citations = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];

        // Use a second AI call to strictly format the text response into JSON
        const formattingResponse = await getAi().models.generateContent({
            model,
            contents: `Based on the following text, extract up to 3 product deals into a JSON array with objects containing "store", "price", and "url" keys. Text: ${response.text ?? ''}`,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.ARRAY,
                    items: {
                        type: Type.OBJECT,
                        properties: {
                            store: { type: Type.STRING },
                            price: { type: Type.STRING },
                            url: { type: Type.STRING },
                        },
                        required: ["store", "price", "url"]
                    }
                }
            }
        });

        const deals: ProductDeal[] = JSON.parse((formattingResponse.text ?? '').trim());
        return { deals, citations };

    } catch (error) {
        console.error("Error finding product deals:", error);
        throw new Error("Failed to find product deals. The search tool may be unavailable.");
    }
};

export const analyzeLSI = async (lsiData: LsiAnalysisInput): Promise<string> => {
    const systemInstruction = `You are a pool chemistry expert. You will be given a calculated Langelier Saturation Index (LSI) value and the individual factors that produced it. Your task is to explain what the LSI value means in simple terms and suggest which specific parameter(s) the user should adjust to bring the LSI into the ideal range of -0.3 to +0.3. Be specific and helpful.`;
    const condition = lsiData.lsi < -0.3 ? 'corrosive' : lsiData.lsi > 0.3 ? 'scaling' : 'balanced';
    const prompt = `The calculated LSI is ${lsiData.lsi.toFixed(2)}, which is ${condition}. Breakdown: pH=${lsiData.ph}, Temp Factor=${lsiData.TF.toFixed(2)}, Calcium Factor=${lsiData.CF_Ca.toFixed(2)}, Alkalinity Factor=${lsiData.AF.toFixed(2)} (corrected TA=${lsiData.correctedAlk.toFixed(0)}), TDS Factor=${lsiData.CF_TDS.toFixed(2)}. Raw values: CH=${lsiData.ch} ppm, TA=${lsiData.ta} ppm, CYA=${lsiData.cya} ppm, Temp=${lsiData.temp}°F, TDS=${lsiData.tds} ppm. Please provide an analysis and specific recommendations.`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: { systemInstruction }
        });
        return response.text ?? '';
    } catch (error) {
        console.error("Error analyzing LSI with AI:", error);
        throw new Error("Failed to get an LSI analysis from the AI. Please try again.");
    }
};

export const explainChemistryConcept = async (concept: string, context: string): Promise<string> => {
    const systemInstruction = `You are a friendly and knowledgeable chemistry teacher who specializes in explaining complex pool water chemistry concepts in a simple, easy-to-understand way for beginners. Use analogies and simple language. Do not just repeat the provided context.`;
    const prompt = `Please explain the concept of "${concept}" in more detail. Here is a basic description for context: "${context}". Elaborate on why it's important and how it interacts with other chemicals in the pool.`;

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: { systemInstruction }
        });
        return response.text ?? '';
    } catch (error) {
        console.error("Error explaining chemistry concept:", error);
        throw new Error("Failed to get an explanation from the AI. Please try again.");
    }
};

// ─── Weekly Dosing Guide ─────────────────────────────────────────────────────

export interface WeeklyDosingDay {
  day: string;
  tasks: string[];
  chemicals: { name: string; dose: string; instructions: string }[];
  notes?: string;
}

export interface WeeklyDosingGuide {
  weekStarting: string;
  waterBody: string;
  poolVolume: number;
  summary: string;
  days: WeeklyDosingDay[];
  weeklyGoals: string[];
  alertsAndWarnings: string[];
}

const weeklyDosingSchema = {
  type: Type.OBJECT,
  properties: {
    weekStarting: { type: Type.STRING, description: 'The date this weekly plan starts (e.g., "Monday, Feb 24, 2026").' },
    waterBody: { type: Type.STRING, description: 'Pool or Hot Tub / Spa.' },
    poolVolume: { type: Type.NUMBER, description: 'Volume in gallons.' },
    summary: { type: Type.STRING, description: 'A 2–3 sentence expert summary of the current water condition and what this week focuses on.' },
    days: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          day: { type: Type.STRING, description: 'Day name (e.g., Monday).' },
          tasks: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'Physical maintenance tasks for the day (e.g., "Skim surface", "Brush walls").' },
          chemicals: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                dose: { type: Type.STRING, description: 'Exact dose, ALWAYS including the metric equivalent in parentheses, e.g. "16 fl oz (473 ml)" or "1.5 lbs (680 g)".' },
                instructions: { type: Type.STRING },
              },
              required: ['name', 'dose', 'instructions'],
            },
            description: 'Chemicals to add this day with exact dose and instructions.',
          },
          notes: { type: Type.STRING, description: 'Optional extra tip or warning for that day.' },
        },
        required: ['day', 'tasks', 'chemicals'],
      },
    },
    weeklyGoals: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'Top 3–5 goals for this week (e.g., "Bring FC to target range").' },
    alertsAndWarnings: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'Any urgent warnings or things to watch for this week.' },
  },
  required: ['weekStarting', 'waterBody', 'poolVolume', 'summary', 'days', 'weeklyGoals', 'alertsAndWarnings'],
};

export const generateWeeklyDosingGuide = async (
  latestResults: TestResult | TestLogEntry,
  poolVolume: number,
  waterBody: WaterBodyType = 'pool',
  history: TestLogEntry[] = [],
): Promise<WeeklyDosingGuide> => {
  const isHotTub = waterBody === 'hot_tub';
  const bodyLabel = isHotTub ? 'hot tub / spa' : 'swimming pool';

  const recentHistory = history.slice(-5);
  let trendBlock = '';
  if (recentHistory.length >= 2) {
    const historyLines = recentHistory.map(h =>
      `  ${h.timestamp}: FC=${h.fc ?? '?'} pH=${h.ph ?? '?'} TA=${h.ta ?? '?'} CH=${h.ch ?? '?'} CYA=${h.cya ?? '?'}`
    ).join('\n');
    trendBlock = `\n\nRecent test history (oldest first):\n${historyLines}`;
  }

  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysToMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() + (dayOfWeek === 1 ? 0 : daysToMonday === 7 ? 0 : -dayOfWeek + 1));
  const weekStart = monday.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const systemInstruction = `You are a world-class pool and spa water chemistry expert. Generate a detailed 7-day (Monday–Sunday) weekly additive dosing and maintenance plan for a ${poolVolume.toLocaleString()}-gallon ${bodyLabel}.

Base the plan on the provided test results and history. Your plan should:
1. IDENTIFY which parameters need correction and schedule dosing on appropriate days (don't add everything on the same day).
2. SPREAD chemical additions throughout the week — avoid adding multiple chemicals at once unless safe.
3. SCHEDULE retesting: after adding alkalinity adjusters, retest in 6–24 hrs; pH adjusters, 1–2 hrs; chlorine shock, 8–24 hrs.
4. INCLUDE routine maintenance tasks each day (skimming, pump checks, brush days, vacuum days, filter inspection).
5. For ${isHotTub ? 'hot tubs' : 'pools'}: ${isHotTub ? 'include weekly water testing, shock every 1–2 weeks, drain/refill reminder if TDS is high or water is old' : 'include backwash reminder, weekly brush/vacuum, monthly deep filter clean'}.
6. IMPORTANT CHEMICAL INTERACTIONS — Never add on the same day:
   - Chlorine + Muriatic Acid (wait 30 min between)
   - Shock + Algaecide (wait 24 hrs)
   - Multiple large dose adjustments (stagger by at least 24 hrs)
7. If chlorine is low, schedule shock at NIGHT or DUSK for best effectiveness.
8. Provide EXACT doses calculated for ${poolVolume} gallons. ${METRIC_DOSE_RULE}
9. Use the "alertsAndWarnings" field for anything urgent (e.g., very low sanitizer = do not use water).

Respond ONLY with the JSON object conforming to the schema.`;

  const testedOnly = testedFields(latestResults);
  const prompt = `Generate a weekly dosing and maintenance plan starting ${weekStart} for my ${poolVolume}-gallon ${bodyLabel}.\n\nLatest test results (only tested parameters shown): ${JSON.stringify(testedOnly)}${trendBlock}\n\nOnly schedule chemical additions for the parameters listed above. Do not recommend corrections for parameters that were not tested.`;

  const response = await getAi().models.generateContent({
    model,
    contents: prompt,
    config: {
      systemInstruction,
      responseMimeType: 'application/json',
      responseSchema: weeklyDosingSchema,
    },
  });

  const jsonText = (response.text ?? '').trim();
  if (!jsonText) throw new Error('AI returned an empty response for the weekly dosing guide.');
  return JSON.parse(jsonText) as WeeklyDosingGuide;
};

export const generateMaintenanceCalendar = async (location: string, log: TestLogEntry[], year: number, month: number): Promise<{ [key: string]: string[] }> => {
    const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });
    const systemInstruction = `You are an expert AI pool maintenance scheduler. Your goal is to create a specific, tailored monthly maintenance calendar.
- First, analyze the user's location and the requested month/year to determine if the pool is likely open for the swimming season or closed/winterized.
- If the pool is likely winterized (e.g., a northern location in January), only schedule essential winter tasks, such as "Check water level under cover" or "Inspect cover for damage" once or twice a month. Do NOT schedule daily skimming or weekly testing.
- If the pool is open, schedule standard routine tasks: Daily (e.g., "Skim surface", "Check baskets"), Weekly (e.g., "Test water", "Brush walls", "Vacuum"), and one Monthly task (e.g., "Deep clean filter").
- Then, analyze the provided test log data. Look for trends, recent adjustments, or recurring problems.
- Based on the log, add specific, actionable tailored tasks. For example: if the last test had high CC, schedule a "Shock pool" task. If the user's pH is consistently high, add an extra "Check pH and TA" task mid-week. If a chemical was added recently, schedule a re-test for that parameter a few days later.
- Respond ONLY with a JSON array of objects. Each object should have a "date" key (in "YYYY-MM-DD" format) and a "tasks" key (an array of task description strings). Only include objects for days that have tasks.`;

    const prompt = `Generate a maintenance calendar for ${monthName} ${year}. The user is located in ${location}. Here is their recent test log (newest entries are last): ${JSON.stringify(log.slice(-10))}`;

    const maintenanceCalendarSchema = {
      type: Type.ARRAY,
      items: {
          type: Type.OBJECT,
          properties: {
              date: {
                  type: Type.STRING,
                  description: 'The date for the tasks in YYYY-MM-DD format.'
              },
              tasks: {
                  type: Type.ARRAY,
                  items: {
                      type: Type.STRING
                  },
                  description: 'An array of maintenance task strings for that day.'
              }
          },
          required: ["date", "tasks"]
      }
    };

    try {
        const response = await getAi().models.generateContent({
            model,
            contents: prompt,
            config: {
                systemInstruction,
                responseMimeType: "application/json",
                responseSchema: maintenanceCalendarSchema,
            }
        });
        const jsonText = (response.text ?? '').trim();
        const parsedResponse = JSON.parse(jsonText) as Array<{ date: string; tasks: string[] }>;

        const calendarData: { [key: string]: string[] } = {};
        if (Array.isArray(parsedResponse)) {
            for (const entry of parsedResponse) {
                if (entry.date && entry.tasks) {
                    calendarData[entry.date] = entry.tasks;
                }
            }
        }

        return calendarData;
    } catch (error) {
        console.error("Error generating maintenance calendar:", error);
        throw new Error("Failed to generate the AI maintenance calendar.");
    }
};
